import axios from 'axios';

class CommentService {
  constructor() {
    // Use environment variable or fallback to local development
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('CommentService initialized with baseURL:', this.baseURL);
  }

    /**
   * Get comment count for a blog
   * @param {string} blogId - The blog ID
   * @returns {Promise} Response data with comment count
   */
  async getCommentCount(blogId) {
    try {
      const response = await axios.get(`${this.baseURL}/comments/count/get?blogId=${blogId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting comment count:', error);
      throw error;
    }
  }

  /**
   * Get comments for a blog
   * @param {string} blogId - The blog ID
   * @returns {Promise} Response data
   */
  async getComments(blogId) {
    try {
      const response = await axios.get(`${this.baseURL}/comment/get?blogId=${blogId}`);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data.data;
      }
      
      return response.data || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  }

  /**
   * Post a new comment
   * @param {string} blogId - The blog post ID
   * @param {Object} commentData - Comment data including text, username, userId, etc.
   * @returns {Promise} Response data
   */
  async postComment(blogId, commentData) {
    try {
      const comment = {
        blogId: blogId,
        text: commentData.text || commentData.comment,
        username: commentData.username || commentData.userName || commentData.displayName,
        displayName: commentData.displayName || commentData.username || commentData.userName,
        firstName: commentData.firstName || '',
        lastName: commentData.lastName || '',
        userId: commentData.userId,
        userEmail: commentData.userEmail || '',
        avatar: commentData.avatar || commentData.photoURL || commentData.picture || ''
      };

      const response = await axios.post(`${this.baseURL}/comment/post`, comment);
      return response.data;
    } catch (error) {
      console.error('Error posting comment:', error);
      throw error;
    }
  }

  /**
   * Delete a comment
   * @param {string} commentId - The comment ID
   * @param {string} blogId - The blog ID
   * @returns {Promise} Response data
   */
  async deleteComment(commentId, blogId) {
    try {
      const response = await axios.delete(`${this.baseURL}/comments/delete?blogId=${blogId}&commentId=${commentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }

  /**
   * Format comment data for display
   * @param {Object} comment - Raw comment data
   * @returns {Object} Formatted comment
   */
  formatComment(comment) {
    return {
      id: comment.id,
      text: comment.text || '',
      username: comment.username || 'Utilizator anonim',
      userId: comment.userId,
      avatar: comment.avatar || '/api/placeholder/40/40',
      createDate: comment.createDate,
      updateDate: comment.updateDate,
    };
  }

  /**
   * Get relative time string for comment date
   * @param {string} dateString - ISO date string
   * @returns {string} Relative time string
   */
  getRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Acum câteva secunde';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minut${minutes !== 1 ? 'e' : ''} în urmă`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} or${hours !== 1 ? 'e' : 'ă'} în urmă`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} zi${days !== 1 ? 'le' : ''} în urmă`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} lun${months !== 1 ? 'i' : 'ă'} în urmă`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} an${years !== 1 ? 'i' : ''} în urmă`;
    }
  }

  /**
   * Like or unlike a comment
   * @param {string} commentId - The comment ID
   * @param {string} blogId - The blog ID
   * @param {string} userId - The user ID
   * @param {string} action - 'like' or 'unlike'
   * @returns {Promise} Response data
   */
  async likeComment(commentId, blogId, userId, action) {
    try {
      const response = await axios.post(`${this.baseURL}/comments/likes`, {
        blogId,
        commentId,
        userId,
        action
      });
      return response.data;
    } catch (error) {
      console.error('Error liking comment:', error);
      throw error;
    }
  }

  /**
   * Get comment likes count and user like status
   * @param {string} commentId - The comment ID
   * @param {string} blogId - The blog ID
   * @param {string} userId - The user ID (optional)
   * @returns {Promise} Response data
   */
  async getCommentLikes(commentId, blogId, userId) {
    try {
      const params = new URLSearchParams({ blogId, commentId });
      if (userId) params.append('userId', userId);
      
      const response = await axios.get(`${this.baseURL}/comments/likes/get?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error getting comment likes:', error);
      throw error;
    }
  }

  /**
   * Add a reply to a comment
   * @param {string} blogId - The blog ID
   * @param {string} commentId - The parent comment ID
   * @param {Object} replyData - Reply data including text, username, userId, etc.
   * @returns {Promise} Response data
   */
  async postReply(blogId, commentId, replyData) {
    try {
      const reply = {
        blogId,
        commentId,
        text: replyData.text,
        username: replyData.username || replyData.displayName,
        displayName: replyData.displayName || replyData.username,
        userId: replyData.userId,
        userEmail: replyData.userEmail || '',
        avatar: replyData.avatar || replyData.photoURL || replyData.picture || ''
      };

      const response = await axios.post(`${this.baseURL}/comments/replies`, reply);
      return response.data;
    } catch (error) {
      console.error('Error posting reply:', error);
      throw error;
    }
  }

  /**
   * Get replies for a comment
   * @param {string} commentId - The comment ID
   * @param {string} blogId - The blog ID
   * @returns {Promise} Response data
   */
  async getReplies(commentId, blogId) {
    try {
      const response = await axios.get(`${this.baseURL}/comments/replies/get?blogId=${blogId}&commentId=${commentId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting replies:', error);
      throw error;
    }
  }

  /**
   * Delete a reply
   * @param {string} replyId - The reply ID
   * @param {string} commentId - The comment ID
   * @param {string} blogId - The blog ID
   * @returns {Promise} Response data
   */
  async deleteReply(replyId, commentId, blogId) {
    try {
      const response = await axios.delete(`${this.baseURL}/comments/replies/delete?blogId=${blogId}&commentId=${commentId}&replyId=${replyId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting reply:', error);
      throw error;
    }
  }
}

const commentService = new CommentService();
export default commentService;
